import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import "./ConfirmModal.css";

export default function ConfirmModal({
  isOpen,
  title = "Confirm",
  message = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmTone = "warning", // warning | danger | neutral
  onConfirm,
  onCancel,
  overlayClassName = "",
}) {
  const titleId = useId();
  const lastActiveElRef = useRef(null);
  const cancelBtnRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    lastActiveElRef.current =
      typeof document !== "undefined" ? document.activeElement : null;

    // Move focus into the modal for keyboard users.
    if (cancelBtnRef.current) cancelBtnRef.current.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel?.();
      }
      if (e.key === "Tab") {
        const buttons = modalRef.current?.querySelectorAll('button:not([disabled])');
        if (!buttons?.length) return;
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      const el = lastActiveElRef.current;
      if (el && typeof el.focus === "function") el.focus();
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const confirmClass = [
    "tf-confirm-btn",
    "tf-confirm-confirm",
    confirmTone === "danger"
      ? "is-danger"
      : confirmTone === "neutral"
        ? "is-neutral"
        : "",
  ]
    .filter(Boolean)
    .join(" ");

  // 사이드바의 backdrop-filter 안에 갇히지 않도록 body 로 포털 렌더링
  return createPortal(
    <div className={`tf-confirm-overlay ${overlayClassName}`.trim()} onClick={onCancel}>
      <div className="tf-confirm-border" onClick={(e) => e.stopPropagation()}>
        <div
          className="tf-confirm-modal"
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div className="tf-confirm-head">
            <h3 className="tf-confirm-title" id={titleId}>
              {title}
            </h3>
          </div>

          {message ? <div className="tf-confirm-message">{message}</div> : null}

          <div className="tf-confirm-actions">
            <button
              ref={cancelBtnRef}
              type="button"
              className="tf-confirm-btn tf-confirm-cancel"
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className={confirmClass}
              onClick={onConfirm}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
